import { getAt } from '@finn-no/mobility-item-insertion-shared';
import { type Locale, useFormatNumber } from '@finn-no/number-format';
import { Affix, TextField } from '@warp-ds/react';
import React from 'react';
import { Controller, type FieldError, type UseControllerProps, useFormContext } from 'react-hook-form';
import type { TextFieldWidgetModel } from '../../../types/widget.js';
import { WidgetTypes } from '../../../types/widget-types.js';

// TODO: Having to omit 'onChange' here is hacky, as we force it to be able to handle the value null
export interface TextFieldWidgetProps extends Omit<TextFieldWidgetModel, 'onChange'> {
  onChange?: (value: string | number | null) => void;
  locale: Locale;
}

const generateAffix = (prefix: string | undefined, suffix: string | undefined) => {
  if (prefix && suffix) {
    return (
      <>
        <Affix prefix label={prefix} />
        <Affix suffix label={suffix} />
      </>
    );
  }
  if (prefix) {
    return <Affix prefix label={prefix} />;
  }
  if (suffix) {
    return <Affix suffix label={suffix} />;
  }
  return undefined;
};

const getInputPattern = (type: TextFieldWidgetModel['type']) => {
  switch (type) {
    case WidgetTypes.NUMBER:
      return '[0-9\u00A0.]*'; // Allow digits and non-breaking spaces
    case WidgetTypes.DECIMAL:
      return '[0-9]*[.,]?[0-9]*'; // Allow only digits, commas and decimal points
    default:
      return undefined;
  }
};

const getInputMode = (type: TextFieldWidgetModel['type']) => {
  switch (type) {
    case WidgetTypes.POSTALCODE:
    case WidgetTypes.NUMBER:
      return 'numeric';
    case WidgetTypes.DECIMAL:
      return 'decimal';
    case WidgetTypes.PHONE:
      return 'tel';
    default:
      return 'text';
  }
};

export const TextFieldWidget = React.forwardRef<HTMLInputElement, TextFieldWidgetProps>(
  ({ mandatory, prefix, suffix, type, name, formatAsNumber, saveAsString, value, onChange, locale, ...rest }, ref) => {
    const removeNonNumeric = (value: string) => value.replace(/[^\d]/g, '');
    const removeNonNumericAsInt = (value: string) => Number.parseInt(removeNonNumeric(value));

    const resolveValue = (type: TextFieldWidgetModel['type'], value: string) => {
      const stringValue = value.toString();
      switch (type) {
        case WidgetTypes.POSTALCODE:
          return removeNonNumeric(stringValue);
        case WidgetTypes.NUMBER: {
          const parsed = removeNonNumericAsInt(stringValue);
          return Number.isNaN(parsed) ? null : saveAsString ? parsed.toString() : parsed;
        }
        default:
          return value;
      }
    };

    const formatValue = (value: string) => {
      const stringValue = value.toString();
      if (formatAsNumber) {
        // biome-ignore lint/correctness/useHookAtTopLevel: not a react hook, but a utility function
        const toFormattedNumber = useFormatNumber(locale);
        const parsedValue = Number.parseFloat(stringValue.replace(/[^\d.,]/g, '').replace(',', '.')); // Handle decimal points
        return Number.isNaN(parsedValue) ? value : toFormattedNumber(parsedValue);
      }
      return value;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const resolvedValue = resolveValue(type, rawValue);

      onChange?.(resolvedValue);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedData = e.clipboardData.getData('Text');
      const resolvedValue = resolveValue(type, pastedData);
      onChange?.(resolvedValue);
      e.preventDefault();
    };

    return (
      <TextField
        {...rest}
        data-testid={name}
        optional={!mandatory}
        aria-required={mandatory}
        ref={ref}
        type="text"
        inputMode={getInputMode(type)}
        pattern={getInputPattern(type)}
        data-attribute={name}
        value={formatValue(value?.toString() ?? '')}
        onChange={handleChange}
        onPaste={handlePaste}
      >
        {generateAffix(prefix, suffix)}
      </TextField>
    );
  },
);

// TODO: Move this "Controlled" part out - it seems to be a pattern that is repeated in all widgets
export const ControlledTextFieldWidget = ({
  name,
  helpText,
  rules,
  ...props
}: TextFieldWidgetProps & UseControllerProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = getAt<FieldError>(errors, name);
  if (error) {
    helpText = (error.message as string) ?? 'Validation error';
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => <TextFieldWidget {...field} invalid={!!error} helpText={helpText} {...props} />}
      rules={rules}
      shouldUnregister={name !== 'price.registration_price'} // TODO: Reference to a specific field in a generic component is not optimal - consider moving this logic to the presentation model where other field specific logic resides
    />
  );
};
